/**
 * Monaco Editor Shim for CDN Loading
 * 
 * This module provides a bridge between ES module imports and Monaco's
 * CDN-based global loading pattern. 
 * 
 * Monaco is loaded asynchronously from CDN via @monaco-editor/react loader.
 * This shim exports a Proxy that defers property access until window.monaco is available.
 * 
 * Note: Monaco must be loaded first via @monaco-editor/react loader.
 * The loader is configured in monaco-loader.ts.
 */

// Global type declarations for Monaco (no import needed)
// These match the monaco-editor package types for compile-time checking
declare const MonacoGlobal: {
  editor: typeof import('monaco-editor').editor;
  languages: typeof import('monaco-editor').languages;
  Uri: typeof import('monaco-editor').Uri;
  KeyCode: typeof import('monaco-editor').KeyCode;
  KeyMod: typeof import('monaco-editor').KeyMod;
  Position: typeof import('monaco-editor').Position;
  Range: typeof import('monaco-editor').Range;
  Selection: typeof import('monaco-editor').Selection;
  [key: string]: unknown;
};

// Cache the loaded monaco instance
let monacoCache: typeof MonacoGlobal | null = null;

// Promise that resolves when Monaco is loaded
let monacoResolve: (value: typeof MonacoGlobal) => void;
const monacoPromise = new Promise<typeof MonacoGlobal>((resolve) => {
  monacoResolve = resolve;
});

// Called by monaco-loader.ts when Monaco is loaded from CDN
export function _setMonacoLoaded(monaco: typeof MonacoGlobal) {
  monacoCache = monaco;
  monacoResolve(monaco);
}

// Get monaco instance (throws if not loaded)
export function getMonaco(): typeof MonacoGlobal {
  if (!monacoCache) {
    throw new Error('Monaco not loaded yet. Ensure monaco-loader is imported before using Monaco features.');
  }
  return monacoCache;
}

// Wait for Monaco to load (useful for async operations)
export async function waitForMonaco(): Promise<typeof MonacoGlobal> {
  return monacoPromise;
}

// Proxy-based export that delegates to window.monaco
// This allows `import * as monaco from 'monaco-editor'` to work after Monaco loads
const monacoProxy = new Proxy({} as typeof MonacoGlobal, {
  get(_target, prop: string) {
    // If Monaco is loaded, return the property from the cache
    if (monacoCache) {
      return monacoCache[prop as keyof typeof MonacoGlobal];
    }
    
    // If Monaco is being loaded via @monaco-editor/react, check window.monaco
    if (typeof window !== 'undefined') {
      const win = window as unknown as { monaco?: typeof MonacoGlobal };
      if (win.monaco) {
        monacoCache = win.monaco;
        monacoResolve(win.monaco);
        return win.monaco[prop as keyof typeof MonacoGlobal];
      }
    }
    
    // Monaco not loaded yet - return undefined
    // The @monaco-editor/react components handle this gracefully
    console.warn(`Monaco property "${prop}" accessed before Monaco loaded. Monaco will load shortly.`);
    return undefined;
  },
});

// Export the proxy as both default and named exports
// This covers both `import monaco from 'monaco-editor'` and `import { editor } from 'monaco-editor'`
export default monacoProxy;

// Named exports that proxies to monaco
// These will resolve after Monaco is loaded from CDN
export const editor = monacoProxy.editor;
export const languages = monacoProxy.languages;
export const Uri = monacoProxy.Uri;
export const KeyCode = monacoProxy.KeyCode;
export const KeyMod = monacoProxy.KeyMod;
export const Position = monacoProxy.Position;
export const Range = monacoProxy.Range;
export const Selection = monacoProxy.Selection;